/**
 * Utility functions for DropdownSelect component
 */

/**
 * Recursively flattens hierarchical options structure
 * @param {Array} options - Array of option objects
 * @param {number} level - Current nesting level
 * @param {Set} collapsedSet - Set of collapsed option values
 * @returns {Array} Flattened array of options with level property
 */
export const flattenOptions = (options, level = 0, collapsedSet = new Set()) => {
  return options.flatMap((option) => {
    const flat = [{ ...option, level }]
    if (option.children && collapsedSet.has(option.value)) {
      return [...flat, ...flattenOptions(option.children, level + 1, collapsedSet)]
    }
    return flat
  })
}

/**
 * Gets all child values recursively for a given option
 * @param {Object} option - Option object
 * @returns {Array} Array of all child values including the option itself
 */
export const getAllChildValues = (option) => {
  if (!option.children) return [option.value]
  return [option.value, ...option.children.flatMap((child) => getAllChildValues(child))]
}

/**
 * Checks if an option has any selected children
 * @param {Object} option - Option object to check
 * @param {Array} selectedValues - Array of selected values
 * @returns {boolean} True if option has selected children
 */
export const hasSelectedChildren = (option, selectedValues) => {
  if (!option.children) return false

  const childValues = option.children.flatMap((child) => getAllChildValues(child))
  return childValues.some((value) => selectedValues.includes(value))
}

/**
 * Filters options to show only selected items and their parents
 * @param {Array} options - Array of option objects
 * @param {Array} selectedValues - Array of selected values
 * @returns {Array} Filtered array of options
 */
export const filterOptionsForViewSelected = (options, selectedValues) => {
  return options.filter((option) => {
    return selectedValues.includes(option.value) || hasSelectedChildren(option, selectedValues)
  })
}

/**
 * Creates a Map of all options for quick lookup
 * @param {Array} options - Array of option objects
 * @returns {Map} Map with option values as keys and option objects as values
 */
export const createOptionsMap = (options) => {
  const map = new Map()

  const walk = (opts) => {
    for (const opt of opts) {
      map.set(opt.value, opt)
      if (opt.children) walk(opt.children)
    }
  }

  walk(options)
  return map
}

/**
 * Filters options based on search query
 * @param {Array} options - Array of option objects
 * @param {string} searchQuery - Search string
 * @returns {Array} Filtered options
 */
export const filterOptionsBySearch = (options, searchQuery) => {
  if (!searchQuery.trim()) return { filteredOptions: options, parentsToExpand: [] }

  const lowerQuery = searchQuery.toLowerCase()
  const filteredOptions = []
  const parentsToExpand = []

  options.forEach((option) => {
    const parentMatches = option.name?.toLowerCase().includes(lowerQuery)
    if (parentMatches) {
      filteredOptions.push({ ...option, children: undefined })
    }
    if (option.children) {
      option.children.forEach((child) => {
        if (child.name?.toLowerCase().includes(lowerQuery)) {
          filteredOptions.push({ ...child })
        }
      })
    }
  })

  return { filteredOptions, parentsToExpand }
}
